import axios from 'axios';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';

// Configure axios to include CSRF token in requests
axios.defaults.withCredentials = true;

class AuthService {
    constructor() {
        this.csrfToken = null;
        // Don't initialize CSRF in constructor to avoid blocking
    }

    // Initialize CSRF token
    async initializeCSRF() {
        try {
            const response = await axios.get(`${API_BASE_URL}/csrf-token`);
            this.csrfToken = response.data.csrfToken;
            // Set CSRF token in axios headers
            axios.defaults.headers.common['X-CSRF-Token'] = this.csrfToken;
            return this.csrfToken;
        } catch (error) {
            console.warn('Failed to initialize CSRF token:', error);
            return null;
        }
    }

    // Get fresh CSRF token if needed
    async refreshCSRF() {
        await this.initializeCSRF();
    }
    // Login function that handles different user types
    async login(emailOrPhone, password) {
        try {
            const loginData = {};
            
            // Check if it's a phone number (customer) or email (restaurant/admin)
            if (/^\d{10}$/.test(emailOrPhone)) {
                // Phone number - customer login
                loginData.phoneNumber = emailOrPhone;
            } else {
                // Email - restaurant or admin login
                loginData.email = emailOrPhone;
            }
            
            loginData.password = password;
            
            const response = await axios.post(API_ENDPOINTS.AUTH_LOGIN, loginData);
            
            if (response.data.user) {
                const userData = {
                    ...response.data.user,
                    token: response.data.token
                };
                
                const result = { user: userData };
                
                // If restaurant data is included, add it to the result
                if (response.data.restaurant) {
                    result.restaurant = response.data.restaurant;
                }
                
                // Include password reset requirement flag
                if (response.data.requiresPasswordReset !== undefined) {
                    result.requiresPasswordReset = response.data.requiresPasswordReset;
                }
                
                // After successful login, initialize CSRF for future requests
                await this.initializeCSRF();
                
                return result;
            }
        } catch (error) {
            console.error('Login error:', error);
            throw new Error(error.response?.data?.message || 'Login failed');
        }
        
        throw new Error('Invalid credentials');
    }

    // Register new user
    async register(userData) {
        try {
            const response = await axios.post(API_ENDPOINTS.AUTH_REGISTER, userData);
            return response.data;
        } catch (error) {
            throw error.response?.data?.message || 'Registration failed';
        }
    }

    // Register new customer
    async registerCustomer(customerData) {
        try {
            const response = await axios.post(API_ENDPOINTS.AUTH_REGISTER_CUSTOMER, customerData);
            return response.data;
        } catch (error) {
            throw error.response?.data?.message || 'Customer registration failed';
        }
    }

    // Create super admin
    async createSuperAdmin() {
        try {
            const response = await axios.post(API_ENDPOINTS.AUTH_CREATE_SUPERADMIN);
            return response.data;
        } catch (error) {
            throw error.response?.data?.message || 'Super admin creation failed';
        }
    }

    // Verify token
    async verifyToken(token) {
        try {
            const response = await axios.post(API_ENDPOINTS.AUTH_VERIFY_TOKEN, { token });
            return response.data;
        } catch (error) {
            throw error.response?.data?.message || 'Token verification failed';
        }
    }

    // Get auth token from localStorage
    getToken() {
        return localStorage.getItem('authToken');
    }

    // Set auth token in localStorage
    setToken(token) {
        localStorage.setItem('authToken', token);
    }

    // Remove auth token from localStorage
    removeToken() {
        localStorage.removeItem('authToken');
    }

    // Check if user is authenticated
    isAuthenticated() {
        return !!this.getToken();
    }
}

const authService = new AuthService();
export default authService;

